import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { SDManager } from './SDManager'
import { D3SimulationManager } from './D3SimulationManager'
import ToggleSwitch2 from '../ToggleSwitch2'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCompress, faExpand } from '@fortawesome/free-solid-svg-icons'

export interface NetworkView3Ref {
    sdManager: SDManager | null
}

const NetworkView = forwardRef<NetworkView3Ref>((_, ref) => {
    const [showActiveOnly, setShowActiveOnly] = useState(false)
    const svgRef = useRef<SVGSVGElement | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const [sdManager, setSdManager] = useState<SDManager | null>(null)
    const [dimensions, setDimensions] = useState<{
        width: number
        height: number
    } | null>(null)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showStatusInfo] = useState(true)

    // Expose the sdManager to the parent component.
    useImperativeHandle(
        ref,
        () => ({
            sdManager,
            showStatusInfo,
        }),
        [sdManager, showStatusInfo],
    )

    useEffect(() => {
        if (!svgRef.current) return

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0]
            const { width, height } = entry.contentRect
            if (width && height) {
                setDimensions({ width, height })
            }
        })

        observer.observe(svgRef.current)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!svgRef.current || !dimensions || sdManager) return

        const simManager = new D3SimulationManager(
            svgRef.current,
            dimensions.width,
            dimensions.height,
        )
        const newSdManager = new SDManager(simManager)
        newSdManager.setShowStatusInfo(showStatusInfo)
        setSdManager(newSdManager)
    }, [dimensions, sdManager, showStatusInfo])

    const toggleFullscreen = () => {
        const el = containerRef.current
        if (!el) return

        if (!document.fullscreenElement) {
            el.requestFullscreen?.()
        } else {
            document.exitFullscreen?.()
        }
    }

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement)
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () =>
            document.removeEventListener(
                'fullscreenchange',
                handleFullscreenChange,
            )
    }, [])

    // Toolbar handlers
    function handleShowActiveOnlyChange(checked: boolean) {
        if (sdManager) {
            sdManager.setShowEnabledOnly(checked)
        }
        setShowActiveOnly(checked)
    }

    return (
        <div
            ref={containerRef}
            className={`relative w-full ${
                isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'h-[800px]'
            }`}
        >
            <div className="mb-2">
                <ToggleSwitch2
                    label="Show Active Only:"
                    checked={showActiveOnly}
                    onChange={handleShowActiveOnlyChange}
                />
            </div>
            <svg
                ref={svgRef}
                className={`border bg-white rounded-lg w-full ${
                    isFullscreen ? 'h-full' : 'h-[770px]'
                }`}
                viewBox={
                    dimensions
                        ? `0 0 ${dimensions.width} ${dimensions.height}`
                        : undefined
                }
                preserveAspectRatio="xMidYMid meet"
            />

            {/* Fullscreen Toggle Button */}
            <button
                onClick={toggleFullscreen}
                className={`absolute bottom-4 right-4 bg-black bg-opacity-60 text-white p-2 rounded-full shadow-md hover:bg-opacity-80 transition-all z-50 text-lg`}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
                <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
            </button>
        </div>
    )
})

export default NetworkView
